// react
import { useMemo } from 'react';
import styled from 'styled-components';

// models
import { ClientDetails, FieldType, ReferralFormType } from '@karehero/models';

// components
import { Card, Title } from '@/components/atoms';
import { createFieldHelper } from '@/components/organisms/EditorFields/fieldHelper';
import { EditorFields } from '@/components/organisms/EditorFields/EditorFields';

// theme
import { breakpointValues } from '@/theme/breakpoint';

// hooks
import { SetIsValid, useValidator, useWindowSize } from '@/hooks';

const nameFields = createFieldHelper<ClientDetails>({
  firstName: {
    label: 'First Name',
    placeholder: 'Joe',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
    validationRules: [
      {
        type: 'name',
      },
      {
        type: 'min-length',
        value: 2,
      },
    ],
  },
  lastName: {
    label: 'Last Name',
    placeholder: 'Bloggs',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
    validationRules: [
      {
        type: 'name',
      },
      {
        type: 'min-length',
        value: 2,
      },
    ],
  },
});

const contactFields = createFieldHelper<ClientDetails>({
  email: {
    label: 'Email',
    placeholder: 'joe.bloggs@example.com',
    isRequired: true,
    field: {
      type: FieldType.Email,
    },
    validationRules: [{ type: 'email' }],
  },
  phoneNumber: {
    label: 'Phone Number',
    placeholder: '07123456789',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
    validationRules: [{ type: 'phone' }],
  },
});

const sjpRecipientDetails = createFieldHelper<ClientDetails>({
  nameOfPersonReceivingCare: {
    label: 'Name of person receiving care',
    placeholder: 'Simon',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
    validationRules: [
      {
        type: 'name',
      },
      {
        type: 'min-length',
        value: 2,
      },
    ],
  },
  contextAboutSituation: {
    label: `Context about the situation (KareHero can also cover your client's Annual Review - please share their Annual Review date below if available)`,
    placeholder:
      "Share any relevant details about your client's current situation",
    isRequired: true,
    field: {
      type: FieldType.TextArea,
    },
    validationRules: [
      {
        type: 'min-length',
        value: 1,
      },
    ],
  },
});

const ifaRecipientDetails = createFieldHelper<ClientDetails>({
  nameOfPersonReceivingCare: {
    label: 'Name of person receiving care',
    placeholder: 'Simon',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
    validationRules: [
      {
        type: 'name',
      },
      {
        type: 'min-length',
        value: 2,
      },
    ],
  },
  contextAboutSituation: {
    label: `Context about the situation`,
    placeholder:
      "Any relevant details about your client's current care situation",
    isRequired: true,
    field: {
      type: FieldType.TextArea,
    },
    validationRules: [
      {
        type: 'min-length',
        value: 1,
      },
    ],
  },
});

/**
 * SjpClientForm renders the account settings page.
 */
export const SjpClientForm = ({
  formType = ReferralFormType.SjpIFA,
  value,
  setValue,
  noText,
  setIsValid,
}: SjpClientFormProps) => {
  // hooks
  const [width] = useWindowSize();
  const { createSetIsValid } = useValidator(setIsValid);

  const isMobile = useMemo(() => width < breakpointValues.mdValue, [width]);

  return (
    <Wrapper>
      {!noText && <Title isNoMargin>Tell us about your client</Title>}
      {!noText && (
        <Description>
          KareHero can support clients and their families at any stage of the
          care journey, whether actively searching for care, or just thinking
          about the future.
        </Description>
      )}
      <Card color='blue' isBordered={false} isPaddingSm>
        <FieldWrapper>
          <EditorFields
            fields={nameFields}
            value={value}
            onChange={(f) => setValue({ ...value, ...f })}
            isRow={!isMobile}
            setIsValid={createSetIsValid('nameFields')}
          />
          <EditorFields
            fields={contactFields}
            value={value}
            onChange={(f) => setValue({ ...value, ...f })}
            isRow={!isMobile}
            setIsValid={createSetIsValid('contactFields')}
          />
          <EditorFields
            fields={
              formType === ReferralFormType.SjpIFA
                ? sjpRecipientDetails
                : ifaRecipientDetails
            }
            value={value}
            onChange={(f) => setValue({ ...value, ...f })}
            setIsValid={createSetIsValid('recipientDetails')}
          />
        </FieldWrapper>
      </Card>
    </Wrapper>
  );
};

// styles

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${(props) => props.theme.spacing[16]};
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[32]};
`;

const Description = styled.div`
  font: ${(props) => props.theme.typography.body.sm.regular};
  color: ${(props) => props.theme.color.neutral[90]};
`;

// types
interface SjpClientFormProps {
  value: Partial<ClientDetails>;
  setValue: (v: Partial<ClientDetails>) => void;
  noText?: boolean;
  setIsValid?: SetIsValid;
  formType: ReferralFormType;
}

SjpClientForm.displayName = 'SjpClientForm';

export default SjpClientForm;
